<template>
	<section class="product-categories-list">
		<header>
			<VInput
				v-model="searchString"
				placeholder="Искать по названию"
				icon-postfix
				clearable
				@input="onSearchStringChange"
			>
				<template #icon>
					<VIcon
						material="search"
						solid
					/>
				</template>
			</VInput>
		</header>

		<article
			v-if="rootProductCategories.length === 0"
			class="no-data"
		>
			Ничего не найдено
		</article>
		<article
			v-else
			class="category-structure"
		>
			<div
				v-for="rootEl in rootProductCategories"
				:key="rootEl.id"
				class="list-item root"
				:class="{
					'show-context': contextMenuProductCategoryId === rootEl.id,
					unpublished: !productCategoriesMap.get(rootEl.id).isPublished,
				}"
			>
				<header
					:class="{ active: modelValue && modelValue.id === rootEl.id }"
				>
					{{ productCategoriesMap.get(rootEl.id).name }}
				</header>

				<VButton
					:id="'c' + rootEl.id"
					class="context-menu-btn"
					theme="flat"
					@click="showContextMenu(rootEl.id)"
				>
					<template #icon>
						<VIcon material="more_vert" />
					</template>
				</VButton>

				<VIcon
					class="unpublished-icon"
					material="visibility_off"
					solid
				/>
				<div class="children">
					<div
						v-for="rootChildEl in rootEl.children"
						:key="rootChildEl.id"
						class="list-item category"
						:class="{
							collapsed: !expandedProductCategories[rootChildEl.id],
							'show-context': contextMenuProductCategoryId === rootChildEl.id,
							unpublished: !productCategoriesMap.get(rootChildEl.id).isPublished || !productCategoriesMap.get(rootEl.id).isPublished,
						}"
					>
						<VIcon
							class="icon-direction"
							material="keyboard_arrow_down"
							solid
						/>

						<header
							:class="{
								active: modelValue && modelValue.id === rootChildEl.id,
							}"
							@click="changeCategoryCollapse(rootChildEl.id)"
						>
							{{ productCategoriesMap.get(rootChildEl.id).name }}
						</header>

						<VButton
							:id="'c' + rootChildEl.id"
							class="context-menu-btn"
							theme="flat"
							@click="showContextMenu(rootChildEl.id)"
						>
							<template #icon>
								<VIcon material="more_vert" />
							</template>
						</VButton>

						<VIcon
							class="unpublished-icon"
							material="visibility_off"
							solid
						/>

						<div
							v-if="expandedProductCategories[rootChildEl.id]"
							class="children"
						>
							<div
								v-for="childId in rootChildEl.children"
								:key="childId"
								class="list-item product"
								:class="{
									'show-context': contextMenuProductCategoryId === childId,
									unpublished: !productCategoriesMap.get(childId).isPublished || !productCategoriesMap.get(rootChildEl.id).isPublished,
								}"
							>
								<header
									:class="{
										active: modelValue && modelValue.id === childId,
									}"
									@click="showEditCategory(childId)"
								>
									{{ productCategoriesMap.get(childId).name }}
								</header>

								<VButton
									:id="'c' + childId"
									class="context-menu-btn"
									theme="flat"
									@click="showContextMenu(childId)"
								>
									<template #icon>
										<VIcon material="more_vert" />
									</template>
								</VButton>

								<VIcon
									class="unpublished-icon"
									material="visibility_off"
									solid
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</article>

		<VPopup
			ref="popup"
			class="category-context-popup"
			:target="contextMenuTarget"
			:visible="contextMenuVisible"
			:offset="0"
			align="right"
			@close="hideContextMenu"
		>
			<template v-if="contextMenuCategory">
				<VButton
					v-if="!contextMenuCategory.isLeaf"
					@click="createCategory"
				>
					<template v-if="contextMenuCategory.parentId === null">
						Добавить категорию
					</template>
					<template v-else>
						Добавить товар
					</template>
				</VButton>
				<VButton
					v-if="contextMenuCategory && contextMenuCategory.isLeaf"
					@click="createCategoryCopy"
				>
					Создать копию
				</VButton>
			</template>
			<VButton @click="showEditCategory(contextMenuProductCategoryId)">
				Редактировать
			</VButton>
			<VButton @click="showApproveDeleteDialog">
				Удалить
			</VButton>
		</VPopup>
	</section>
</template>

<style lang="scss" src="./ProductCategoriesRegistry.scss" />

<script lang="ts">

import debounce                 from 'lodash.debounce';

import ProductCategory          from '@/domains/ProductCategories/ProductCategory';
import ProductCategoriesService from '@/domains/ProductCategories/ProductCategoriesService';

export default defineComponent({
	name: 'ProductCategoriesRegistry',

	props: {
		modelValue: {
			type:    Object as () => ProductCategory | null,
			default: null,
		},
	},

	emits: ['update:modelValue'],

	data() {
		return {
			productCategoriesMap:  ProductCategoriesService.getProductCategoriesMap(),
			searchString:          null,
			rootProductCategories: [],

			/** По хорошему это чистой воды Set, но во Vue 2 такие коллекции как Map, Set, WeakMap не реактивны
			 *  самый простой способ реактивно обновлять объекты это this.$set, this.$delete,
			 * а городить шляпу не хочется, чтобы делать Set реактивным,
			 * так что здесь обычный объект (Это быстрее всего и всем понятно) */
			expandedProductCategories: {},

			contextMenuProductCategoryId: null,
			contextMenuVisible:           false,
		};
	},

	computed: {
		categoryRootParentIdList() {
			const result = [];

			this.productCategoriesMap.forEach((value, key) => {
				if (value.parentId === null) result.push(key);
			});

			return result;
		},
		leafList() {
			const result = [];

			this.productCategoriesMap.forEach(value => {
				if (value.isLeaf) result.push(value);
			});

			return result;
		},

		contextMenuCategory() {
			return this.contextMenuProductCategoryId === null
				? null
				: this.productCategoriesMap.get(this.contextMenuProductCategoryId);
		},
		contextMenuTarget() {
			return this.contextMenuProductCategoryId === null
				? null
				: `#c${this.contextMenuProductCategoryId}`;
		},
	},

	watch: {
		async contextMenuProductCategoryId(newVal, oldVal) {
			if (newVal !== null && oldVal !== null) {
				await this.$nextTick();
				this.$refs.popup.calculatePosition();
			}
		},
	},

	created() {
		this.calculateVisibleCategories();
	},

	methods: {
		async reloadProductCategories() {
			try {
				await ProductCategoriesService.load();
			} catch {
				return;
			}

			this.rootProductCategories = [];

			/** todo После обновления/удаления/создания нужно обновить всю иерархию, но
			 * можно делать это не таким радикальным способом как получением
			 * заново всех сущностей и создание мапы, а просто вложить ещё один
			 * элемент в коллекцию, и обновить те категории для которых текущая обвялятся родителем или
			 * потомком, это было бы гораздо быстрее, чем делать ресет, и заново получать
			 *
			 * Однако же нужно помнить о ProductCategoriesService.load(),
			 * и придумать что-то типа patch метод */

			this.productCategoriesMap = ProductCategoriesService.getProductCategoriesMap();

			this.calculateVisibleCategories();
		},

		setCollapseForAll(collapsed = true) {
			this.productCategoriesMap
				.forEach((value, key) => {
					if (collapsed) {
						this.expandedProductCategories[key] = value;
					} else {
						delete this.expandedProductCategories[key];
					}
				});
		},

		changeCategoryCollapse(categoryId) {
			if (this.expandedProductCategories[categoryId]) {
				delete this.expandedProductCategories[categoryId];
			} else {
				this.expandedProductCategories[categoryId] = this.productCategoriesMap.get(categoryId);
			}
		},

		/** Разворачивает конкретную категорию
		 *
		 * todo переписать на рекурсию
		 * */
		expandCategory(productCategoryId) {
			let _productCategory = this.productCategoriesMap.get(productCategoryId);

			if (!_productCategory.isLeaf) {
				this.expandedProductCategories[productCategoryId] = this.productCategoriesMap.get(productCategoryId);
			}

			while (_productCategory.parentId) {
				this.expandedProductCategories[_productCategory.parentId] = this.productCategoriesMap.get(_productCategory.parentId);
				_productCategory = this.productCategoriesMap.get(_productCategory.parentId);
			}
		},

		calculateVisibleCategories() {

			if (this.$core.utils.string.isNotEmptyString(this.searchString)) {
				const parents = {};
				const rootParents = {};

				// TODO All this logic is experiment, refactor due 15/11/2022
				this.leafList.forEach(category => {
					const re = new RegExp(this.searchString, 'i');

					if (re.test(category.name)) {
						const parentId = this.productCategoriesMap.get(category.id).parentId;
						const rootParentId = this.productCategoriesMap.get(parentId).parentId;

						if (!this.$core.utils.isObject(rootParents[rootParentId])) {
							rootParents[rootParentId] = {
								id:       rootParentId,
								children: [],
							};
						}
						if (!rootParents[rootParentId].children.includes(parentId)) {
							rootParents[rootParentId].children.push(parentId);
						}

						if (!this.$core.utils.isObject(parents[parentId])) {
							parents[parentId] = {
								id:       parentId,
								children: [],
							};
						}
						parents[parentId].children.push(category.id);
					}
				});

				this.setCollapseForAll(true);
				this.setAndSortVisibleCategories(
					Object.keys(rootParents)
						.map(rootParentId => ({
							id:       Number(rootParentId),
							children: rootParents[rootParentId].children.map(parentId => ({
								id:       Number(parentId),
								children: parents[parentId].children,
							})),
						})),
				);
				return;
			}

			this.setCollapseForAll(false);

			this.setAndSortVisibleCategories(
				this.categoryRootParentIdList.map(rootId => {
					const children = this.productCategoriesMap.get(rootId)
						.children
						.map(childId => ({
							id:       Number(childId),
							children: this.productCategoriesMap.get(childId).children,
						}));

					return {
						id: Number(rootId),
						children,
					};
				}),
			);
		},

		onSearchStringChange: debounce(function () {
			this.calculateVisibleCategories();
		}, 400),

		setAndSortVisibleCategories(categories) {

			if (categories.length === 0) {
				this.rootProductCategories = [];
				return;
			}

			categories.sort((a, b) => {
				if (typeof this.productCategoriesMap.get(a.id).name !== 'string') {
					this.productCategoriesMap.get(a.id).name = this.productCategoriesMap.get(a.id)
						.name
						.toString();
				}
				return this.productCategoriesMap.get(a.id)
					.name
					.localeCompare(this.productCategoriesMap.get(b.id).name);
			});
			categories.forEach(category => {
				category.children.sort((a, b) => this.productCategoriesMap.get(a.id)
					.name
					.localeCompare(this.productCategoriesMap.get(b.id).name));
				category.children.forEach(categoryChild => {
					categoryChild.children.sort((a, b) => {
						if (typeof this.productCategoriesMap.get(a).name !== 'string') {
							this.productCategoriesMap.get(a).name = this.productCategoriesMap.get(a)
								.name
								.toString();
						}
						return this.productCategoriesMap.get(a)
							.name
							.localeCompare(this.productCategoriesMap.get(b).name);
					});
				});
			});

			this.rootProductCategories = categories;
		},

		showApproveDeleteDialog() {
			const deleteCategoryId = this.contextMenuProductCategoryId,
				deleteCategory = this.productCategoriesMap.get(deleteCategoryId);

			let deleteCategoryParentId = null;

			if (deleteCategory.isLeaf) {
				deleteCategoryParentId = deleteCategory.parentId;
			}

			this.hideContextMenu();
			this.$core.dialogs.VApproveDialog.show(
				{},
				{
					approve: async () => {
						await ProductCategoriesService.delete(deleteCategoryId);
						await this.reloadProductCategories();

						/** Если это был листок, то нужно взять его родителя, и открыть */
						if (deleteCategoryParentId) {
							this.expandCategory(deleteCategoryParentId);
						}

						this.$core.dialogs.VApproveDialog.hide();
					},
					close: () => {
						this.$core.dialogs.VApproveDialog.hide();
					},
				},
				'Удалить категорию?',
			);
		},

		async showContextMenu(categoryId) {
			this.contextMenuProductCategoryId = categoryId;
			await this.$nextTick();
			this.contextMenuVisible = true;
		},

		hideContextMenu() {
			this.contextMenuVisible = false;
			setTimeout(() => {
				this.contextMenuProductCategoryId = null;
			}, 300);
		},

		async showEditCategory(editCategoryId) {
			/** Service.get уже возвращает гидратированную ProductCategory — повторный fromServer
			 * ломал модель из-за геттера/сеттера parent (рекурсивно лез в карту категорий
			 * и затирал поля при двойном проходе). */
			const category = await ProductCategoriesService.get(editCategoryId, {
				requireAllowedUnits:    true,
				requireCharacteristics: true,
			});

			this.$emit('update:modelValue', category);
			this.hideContextMenu();
		},

		async createCategory() {
			/** contextMenuProductCategoryId лежит айди той категории, чьё контекстное меню пользователь тыкнул,
			 * и можно точно сказать что там лежит айди parent */
			const parent = await ProductCategoriesService.get(this.contextMenuProductCategoryId);

			this.hideContextMenu();
			this.$emit('update:modelValue', ProductCategory.fromParentCategory(parent));
		},

		async createCategoryCopy() {
			this.hideContextMenu();

			const category = await ProductCategoriesService.get(this.contextMenuProductCategoryId);

			this.$emit('update:modelValue', ProductCategory.fromAnotherCategory(category));
		},
	},
});
</script>